import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useImages } from '../context/ImageContext';
import Footer from '../components/Footer';
import { cartStorage, CartItem } from '../utils/cartStorage';
import { pushAddToCart } from '../utils/gtm';
import { OpenGraphHead } from '../components/OpenGraphHead';
import './AccessoryDetailPage.css';

const AccessoryDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { getImage } = useImages();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
  
  const [accessory, setAccessory] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [paymentPlan, setPaymentPlan] = useState<'monthly' | 'annual'>('annual');
  const [accessoryImage, setAccessoryImage] = useState<string>('');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    technical: false,
    shipping: false,
    refund: false
  });
  const [requiresSystemConfirmation, setRequiresSystemConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  useEffect(() => {
    const fetchAccessory = async () => {
      try {
        setLoading(true);
        // Fetch all accessories and find by slug or ID
        const response = await fetch(`${API_URL}/api/accessories`);
        const data = await response.json();
        
        console.log('[AccessoryDetailPage] Accessories API response:', data);
        console.log('[AccessoryDetailPage] Looking for slug:', slug);
        console.log('[AccessoryDetailPage] Available accessories:', data.accessories?.map((acc: any) => ({ id: acc.id, name: acc.name, isActive: acc.isActive })));
        
        let foundAccessory: any = null;
        if (slug && data.accessories) {
          // First, try to match by ID (exact match) - this should work for "accessory-2", "accessory-3", etc.
          foundAccessory = data.accessories.find((acc: any) => acc.id === slug);
          console.log('[AccessoryDetailPage] After ID match:', foundAccessory?.name || 'not found');
          
          // Then try by shortName if it exists (for future compatibility)
          if (!foundAccessory) {
            foundAccessory = data.accessories.find((acc: any) => acc.shortName === slug);
            console.log('[AccessoryDetailPage] After shortName match:', foundAccessory?.name || 'not found');
          }
          
          // Try to match by name (fuzzy match for URL-friendly slugs)
          if (!foundAccessory) {
            const slugLower = slug.toLowerCase().replace(/[-_]/g, ' ');
            foundAccessory = data.accessories.find((acc: any) => {
              const nameLower = acc.name.toLowerCase();
              // Check if slug matches key words from the name
              if (slugLower.includes('protection') && nameLower.includes('protection')) return true;
              if (slugLower.includes('smoke') && nameLower.includes('smoke')) return true;
              if (slugLower.includes('lock') && nameLower.includes('lock')) return true;
              if (slugLower.includes('pendant') && nameLower.includes('pendant')) return true;
              if (slugLower.includes('bracelet') && nameLower.includes('bracelet')) return true;
              if (slugLower.includes('medication') && nameLower.includes('medication')) return true;
              if (slugLower.includes('fall') && nameLower.includes('fall')) return true;
              if (slugLower.includes('gps') && nameLower.includes('gps')) return true;
              return false;
            });
            console.log('[AccessoryDetailPage] After name fuzzy match:', foundAccessory?.name || 'not found');
          }
        }
        
        console.log('[AccessoryDetailPage] Final found accessory:', foundAccessory);
        
        if (foundAccessory) {
          setAccessory(foundAccessory);
          
          // Fetch image
          if (foundAccessory.imageFilename) {
            const imagesResponse = await fetch(`${API_URL}/api/images`);
            const imagesData = await imagesResponse.json();
            
            // For smoke detector, prefer the specific image
            const isSmoke = foundAccessory.name?.toLowerCase().includes('smoke detector');
            if (isSmoke) {
              // Try the specific smoke detector image first
              const smokeImg = imagesData.images?.find((i: any) => 
                i.filename === 'accessories-Smoke-Detector-1—3.jpg' ||
                i.filename === 'accessories-Smoke-Detector-1-3.jpg' ||
                i.filename?.toLowerCase().includes('smoke-detector-1')
              );
              if (smokeImg) {
                setAccessoryImage(`${API_URL}/api/images/${smokeImg.id}`);
              } else {
                // Fall back to imageFilename from database
                const img = imagesData.images?.find((i: any) => 
                  i.filename === foundAccessory.imageFilename ||
                  i.filename?.toLowerCase() === foundAccessory.imageFilename?.toLowerCase()
                );
                if (img) {
                  setAccessoryImage(`${API_URL}/api/images/${img.id}`);
                } else {
                  console.warn('Image not found for smoke detector');
                }
              }
            } else {
              // For other accessories, use imageFilename
              const img = imagesData.images?.find((i: any) => 
                i.filename === foundAccessory.imageFilename ||
                i.filename?.toLowerCase() === foundAccessory.imageFilename?.toLowerCase()
              );
              if (img) {
                setAccessoryImage(`${API_URL}/api/images/${img.id}`);
              } else {
                console.warn('Image not found for filename:', foundAccessory.imageFilename);
              }
            }
          }
        } else {
          console.warn('Accessory not found for slug:', slug);
          console.log('Available accessories:', data.accessories?.map((acc: any) => ({ id: acc.id, name: acc.name })));
        }
      } catch (error) {
        console.error('Error fetching accessory:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAccessory();
  }, [slug, API_URL]);

  const hasSystemInCart = () => {
    const items = cartStorage.getItems();
    return items.some(item => item.type === 'product');
  };

  const requiresCompatibleSystem = (name?: string) => {
    const lower = name?.toLowerCase() || '';
    if (!lower) return false;

    const isSmokeDetector = lower.includes('smoke detector');
    const isPendantActivator = lower.includes('necklace with help button') ||
      lower.includes('pendant with emergency call button') ||
      lower.includes('necklace with fall detection') ||
      lower.includes('fall detection pendant') ||
      lower.includes('fall detection alert pendant') ||
      lower.includes('mobile alert pendant') ||
      lower.includes('mobile fall detection') ||
      lower.includes('medical alert bracelet') ||
      (lower.includes('emergency button alarm') && lower.includes('bracelet'));

    return isSmokeDetector || isPendantActivator;
  };

  useEffect(() => {
    setRequiresSystemConfirmation(false);
    setPendingAction(null);
  }, [accessory?.id]);

  const requestSystemConfirmation = (action: () => void) => {
    setPendingAction(() => action);
    setRequiresSystemConfirmation(true);
  };

  const runWithSystemConfirmation = (action: () => void, name?: string) => {
    const targetName = name ?? accessory?.name;
    if (requiresCompatibleSystem(targetName) && !hasSystemInCart()) {
      requestSystemConfirmation(action);
      return;
    }
    action();
  };

  const cancelSystemConfirmation = () => {
    setPendingAction(null);
    setRequiresSystemConfirmation(false);
  };

  const confirmSystemAndContinue = () => {
    const action = pendingAction;
    cancelSystemConfirmation();
    if (action) {
      action();
    }
  };

  // For Added Protection Plan specifically
  const protectionPlanFeatures = [
    'DISCOUNTED and (unlimited) replacements and repairs on your medical alert system',
    'DISCOUNTED coverage for damage or destruction to device',
    '100% peace of mind for you and your family'
  ];

  const getCurrentPrice = () => {
    if (paymentPlan === 'annual') {
      return 60.00; // $5/month * 12
    }
    return 5.00; // Monthly price
  };

  const formatCurrency = (value?: number | null) => {
    if (value === undefined || value === null) return '';
    return `$${value.toFixed(2)}`;
  };

  const getBillingCycleFromPriceType = (priceType?: string): 'monthly' | 'annual' | 'one-time' => {
    if (priceType === 'monthly') return 'monthly';
    if (priceType === 'annual') return 'annual';
    return 'one-time';
  };

  const getPriceFrequencyLabel = (priceType?: string) => {
    if (priceType === 'monthly') return 'per month';
    if (priceType === 'annual') return 'per year';
    return 'one-time';
  };

  const handleOrderNow = () => {
    if (!accessory) return;

    const isProtectionPlan = accessory.name.toLowerCase().includes('protection plan');
    const addWithSystemCheck = () => runWithSystemConfirmation(() => addAccessoryToCart(), accessory.name);

    // Check if this is a protection plan and if one already exists
    if (isProtectionPlan) {
      const existingCartItems = cartStorage.getItems();
      const existingProtectionPlan = existingCartItems.find(item => 
        item.type === 'accessory' && 
        item.name.toLowerCase().includes('protection plan')
      );

      if (existingProtectionPlan) {
        alert('Protection plan already selected. Please delete the prior protection plan from your cart to choose this one.');
        return;
      }

      // Check if there's a product in cart and if billing cycles match
      const productItem = existingCartItems.find(item => item.type === 'product' && item.planId);
      if (productItem && productItem.planId) {
        // Fetch product to check billing cycle
        fetch(`${API_URL}/api/products`)
          .then(res => res.json())
          .then(data => {
            const product = data.products?.find((p: any) => p.id === productItem.productId);
            if (product) {
              const plan = product.pricingPlans?.find((p: any) => p.id === productItem.planId);
              if (plan && plan.billingCycle !== paymentPlan) {
                alert(`Protection plan must match service plan (monthly or annual). Your service plan is ${plan.billingCycle === 'annual' ? 'annual' : 'monthly'}, but you selected a ${paymentPlan === 'annual' ? 'annual' : 'monthly'} protection plan.`);
                return;
              }
              // Billing cycles match, proceed with adding
              addWithSystemCheck();
            } else {
              addWithSystemCheck();
            }
          })
          .catch(() => addWithSystemCheck());
      } else {
        // No product in cart yet, allow adding protection plan
        addWithSystemCheck();
      }
    } else {
      // Not a protection plan, just add it
      addWithSystemCheck();
    }
  };

  const addAccessoryToCart = () => {
    if (!accessory) return;

    // Add accessory to cart
    const price = getCurrentPrice();
    const cartItem: CartItem = {
      id: `accessory-${accessory.id}-${paymentPlan}`,
      type: 'accessory',
      name: accessory.name,
      imageUrl: accessoryImage,
      accessoryId: accessory.id,
      price,
      billingCycle: paymentPlan
    };

    cartStorage.addItem(cartItem);

    pushAddToCart({
      currency: 'USD',
      value: price,
      items: [{
        item_id: cartItem.id,
        item_name: cartItem.name,
        price,
        quantity: 1,
        index: 0,
      }],
    });

    // Navigate to cart page
    navigate('/cart');
  };

  if (loading) {
    return (
      <div className="accessory-detail-page" style={{ minHeight: '100vh', padding: '2rem' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!accessory) {
    return (
      <div className="accessory-detail-page" style={{ minHeight: '100vh', padding: '2rem' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1>Accessory not found</h1>
          <p>Slug: {slug}</p>
          <button 
            onClick={() => navigate('/accessories')} 
            style={{ 
              padding: '0.75rem 1.5rem', 
              backgroundColor: '#c8102e', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '1rem'
            }}
          >
            Back to Accessories
          </button>
        </div>
      </div>
    );
  }

  // Special handling for Added Protection Plan
  const isProtectionPlan = accessory?.name?.toLowerCase().includes('protection plan');
  const isSmokeDetector = accessory?.name?.toLowerCase().includes('smoke detector') || 
                          (accessory?.name?.toLowerCase().includes('smoke') && accessory?.name?.toLowerCase().includes('detector'));
  const isPendantHelpButton = (accessory?.name?.toLowerCase().includes('necklace with help button') || 
                               accessory?.name?.toLowerCase().includes('pendant with emergency call button')) || false;
  const isLockBox = accessory?.name?.toLowerCase().includes('lock box') || false;
  const isFallDetectionPendant = (accessory?.name?.toLowerCase().includes('necklace with fall detection') ||
                                  accessory?.name?.toLowerCase().includes('fall detection pendant')) || false;
  const isMobileGPSPendant = (accessory?.name?.toLowerCase().includes('mobile alert pendant with gps') ||
                              accessory?.name?.toLowerCase().includes('emergency alert bracelet with 24/7 help button')) || false;
  const isMobileFallDetectionPendant = (accessory?.name?.toLowerCase().includes('mobile fall detection alert pendant with gps') ||
                                        accessory?.name?.toLowerCase().includes('on-the-go with fall detection') ||
                                        (accessory?.name?.toLowerCase().includes('fall detection alert pendant') && accessory?.name?.toLowerCase().includes('gps'))) || false;
  const isMedicalAlertBracelet = (accessory?.name?.toLowerCase().includes('medical alert bracelet') ||
                                  (accessory?.name?.toLowerCase().includes('emergency button alarm') && accessory?.name?.toLowerCase().includes('bracelet'))) || false;
  
  const isMedicationDispenser = (accessory?.name?.toLowerCase().includes('medication dispenser') ||
                                 accessory?.name?.toLowerCase().includes('medication reminder') ||
                                 accessory?.name?.toLowerCase().includes('medication organizer')) || false;
  const isPurpleWristband = (accessory?.name?.toLowerCase().includes('purple wristband') ||
                            accessory?.name?.toLowerCase().includes('purple wrist band')) || false;

  // accessory-6 and accessory-7: do not allow standalone wristband/pendant order (LifeBeacon requires EZ Care device first)
  const isNoStandaloneOrder = accessory?.id === 'accessory-6' || accessory?.id === 'accessory-7';

  // Debug logging to help troubleshoot matching
  console.log('[AccessoryDetailPage] Full accessory object:', accessory);
  console.log('[AccessoryDetailPage] Accessory name:', accessory?.name);
  console.log('[AccessoryDetailPage] Name lowercased:', accessory?.name?.toLowerCase());
  console.log('[AccessoryDetailPage] Smoke detector check:', {
    nameIncludesSmokeDetector: accessory?.name?.toLowerCase().includes('smoke detector'),
    nameIncludesSmoke: accessory?.name?.toLowerCase().includes('smoke'),
    nameIncludesDetector: accessory?.name?.toLowerCase().includes('detector'),
    finalResult: isSmokeDetector
  });
  console.log('[AccessoryDetailPage] All Conditionals:', {
    isProtectionPlan,
    isSmokeDetector,
    isPendantHelpButton,
    isLockBox,
    isFallDetectionPendant,
    isMobileGPSPendant,
    isMobileFallDetectionPendant,
    isMedicalAlertBracelet,
    isMedicationDispenser
  });

  // Smoke Detector Features
  const smokeDetectorFeatures = [
    "We can send for help even if you can't reach a phone, hear the alarm, or are out of the house",
    "Our 24/7 Command Center will come on the line to check for false alarms",
    "3+ year battery life and low battery detection",
    "Works seamlessly with Alert1's Home Medical Alert System",
    "We will stay on the line with you during emergencies"
  ];

  // Handle smoke detector order - two options
  const handleSmokeDetectorOrder = (option: 'monitored' | 'detector-only') => {
    if (!accessory) return;

    const addToCart = () => {
      const cartItem: CartItem = {
        id: `accessory-${accessory.id}-${option}`,
        type: 'accessory',
        name: option === 'monitored' ? `${accessory.name} (Monitored Service)` : `${accessory.name} (Detector Only)`,
        imageUrl: accessoryImage,
        accessoryId: accessory.id,
        price: option === 'monitored' ? 5.00 : 30.00,
        billingCycle: option === 'monitored' ? 'monthly' : 'one-time'
      };

      cartStorage.addItem(cartItem);
      navigate('/cart');
    };

    runWithSystemConfirmation(addToCart, accessory.name);
  };

  // Handle pendant order - two options (free with subscription or $15 button only)
  const handlePendantOrder = (option: 'subscription' | 'button-only') => {
    if (!accessory) return;

    if (option === 'subscription') {
      // Check if there's a product in cart
      const existingCartItems = cartStorage.getItems();
      const hasProduct = existingCartItems.some(item => item.type === 'product');
      
      if (!hasProduct) {
        alert(`Please add a medical alert system to your cart first to get the ${accessory.name} FREE with your subscription.`);
        return;
      }
      
      // Add as free accessory
      const addItem = () => {
        const proceed = window.confirm('Reminder: Fall detection pendants only work when fall detection service is enabled on your Alert1 plan. Do you still want to add this accessory?');
        if (!proceed) {
          return;
        }

        const cartItem: CartItem = {
          id: `accessory-${accessory.id}-subscription`,
          type: 'accessory',
          name: `${accessory.name} (FREE with Subscription)`,
          imageUrl: accessoryImage,
          accessoryId: accessory.id,
          price: 0.00,
          billingCycle: 'one-time'
        };

        cartStorage.addItem(cartItem);
        navigate('/cart');
      };

      runWithSystemConfirmation(addItem, accessory.name);
    } else {
      // Add as $15 button only
      const addItem = () => {
        const cartItem: CartItem = {
          id: `accessory-${accessory.id}-button-only`,
          type: 'accessory',
          name: `${accessory.name} (SOS Button Pendant Only)`,
          imageUrl: accessoryImage,
          accessoryId: accessory.id,
          price: accessory.price ?? 0,
          billingCycle: getBillingCycleFromPriceType(accessory.priceType)
        };

        cartStorage.addItem(cartItem);
        navigate('/cart');
      };

      runWithSystemConfirmation(addItem, accessory.name);
    }
  };

  // Pendant Features
  const pendantFeatures = [
    "SOS help button pendant that works with Alert1's Home Medical Alert System",
    "Push the button & connect to Alert1's 24/7 emergency monitoring center who will send you the help you need",
    "We will stay on the line with you until help arrives-- never face an emergency alone!",
    "Showerproof button, compatible with pacemakers",
    "No Wi-Fi or cell phone needed, works within 600 ft of the base unit. Upgrades to GPS help buttons available."
  ];

  // Lock Box Features
  const lockBoxFeatures = [
    "If you need help sent to your home, our Command Center will give responders the lockbox code",
    "Avoid expensive repairs for broken doors and windows if EMS can't enter your home",
    "Securely hold up to five keys",
    "Locks tightly around doorknobs, fencing, and railings",
    "4 digit code allows access to keys"
  ];

  // Handle lock box order
  const handleLockBoxOrder = () => {
    if (!accessory) return;

    const cartItem: CartItem = {
      id: `accessory-${accessory.id}`,
      type: 'accessory',
      name: accessory.name,
      imageUrl: accessoryImage,
      accessoryId: accessory.id,
      price: 25.00,
      billingCycle: 'one-time'
    };

    cartStorage.addItem(cartItem);
    navigate('/cart');
  };

  // Handle fall detection pendant order - two options (free with subscription or $25 button only)
  const handleFallDetectionPendantOrder = (option: 'subscription' | 'button-only') => {
    if (!accessory) return;

    if (option === 'subscription') {
      // Check if there's a product in cart
      const existingCartItems = cartStorage.getItems();
      const hasProduct = existingCartItems.some(item => item.type === 'product');
      
      if (!hasProduct) {
        alert('Please add a medical alert system to your cart first to get the fall detection pendant FREE with your subscription.');
        return;
      }
      
      // Add as free accessory
      const addItem = () => {
        const proceed = window.confirm('Reminder: Fall detection pendants only work when fall detection service is enabled on your Alert1 plan. Do you still want to add this accessory?');
        if (!proceed) {
          return;
        }

        const cartItem: CartItem = {
          id: `accessory-${accessory.id}-subscription`,
          type: 'accessory',
          name: `${accessory.name} (FREE with Subscription)`,
          imageUrl: accessoryImage,
          accessoryId: accessory.id,
          price: 0.00,
          billingCycle: 'one-time'
        };

        cartStorage.addItem(cartItem);
        navigate('/cart');
      };

      runWithSystemConfirmation(addItem, accessory.name);
    } else {
      // Add as $25 button only
      const addItem = () => {
        const proceed = window.confirm('Reminder: Fall detection pendants only work when fall detection service is enabled on your Alert1 plan. Do you still want to add this accessory?');
        if (!proceed) {
          return;
        }

        const cartItem: CartItem = {
          id: `accessory-${accessory.id}-button-only`,
          type: 'accessory',
          name: `${accessory.name} (Fall Alert Pendant Only)`,
          imageUrl: accessoryImage,
          accessoryId: accessory.id,
          price: 25.00,
          billingCycle: 'one-time'
        };

        cartStorage.addItem(cartItem);
        navigate('/cart');
      };

      runWithSystemConfirmation(addItem, accessory.name);
    }
  };

  // Fall Detection Pendant Features - Section 1
  const fallDetectionFeatures1 = [
    "Additional button for the Alert1 In-Home Fall Detection Medical Alert System",
    "Will send an alert for help automatically if it senses a fall",
    "You decide who we call in an emergency, whether that's family, a neighbor, or emergency service first responders",
    "We will stay on the line with you until help arrives",
    "No Wi-Fi or cell phone required.",
    "Works with home system base unit. Mobile upgrade available",
    "No long term contracts"
  ];

  // Fall Detection Pendant Features - Section 2
  const fallDetectionFeatures2 = [
    "Additional button for the Alert1 Fall Detection Medical Alert System",
    "Will call Alert1's 24/7 Command Center automatically if it senses a fall",
    "You decide who we call in an emergency, whether that's family, a neighbor, or EMS",
    "We will stay on the line with you until help arrives",
    "No Wi-Fi or cell phone required, 600 ft range around the base unit. Mobile upgrade available",
    "For seniors and others with medical alert systems, wearing a medical ID necklace, medical alert pendant, and medical necklace helps first responders and emergency services know what to do and how to respond during a medical emergency.",
    "Pair this fall detection alert pendant with a comprehensive medical alert home system for assistance when you need it."
  ];

  // Mobile GPS Pendant Features
  const mobileGPSFeatures = [
    "Alert1's medical alert bracelet has built-in location services.",
    "Water-resistant wristband alert can be worn while showering.",
    "Optional fall detection feature is available.",
    "Push the emergency alert button to connect to Alert1's 24/7 Command Center in seconds.",
    "2-way speaker is built directly into the device for easy communication."
  ];

  // Mobile Fall Detection Pendant Features
  const mobileFallDetectionFeatures = [
    "Fall alert button alarm works with Alert1's Mobile Fall Detection emergency monitoring",
    "Fall detection device can call for help automatically if it senses a fall",
    "Two-way speaker and microphone are built into the alert pendant",
    "Our 24/7 Command Center will send you the help you need in any emergency",
    "No landline, Wi-Fi, or cell phone needed. This device uses the AT&T and Verizon networks."
  ];

  // Medical Alert Bracelet Features
  const medicalAlertBraceletFeatures = [
    "Our medical alert bracelets connect wirelessly to the Alert1 in-home medical alert system base",
    "No WiFi or cell phone needed. Coverage area extends 600 feet around the medical alert system base station.",
    "Showerproof medical alert button is compatible with pacemakers",
    "Push the medical alert button, get connected to our 24/7/365 Command Center",
    "Monthly or annual plans provide peace of mind for you and your loved ones"
  ];

  // Handle mobile fall detection pendant order - two options (free with subscription or pendant only)
  const handleMobileFallDetectionPendantOrder = (option: 'subscription' | 'pendant-only') => {
    if (!accessory) return;

    if (option === 'subscription') {
      // Check if there's a product in cart
      const existingCartItems = cartStorage.getItems();
      const hasProduct = existingCartItems.some(item => item.type === 'product');
      
      if (!hasProduct) {
        alert(`Please add a medical alert system to your cart first to get the ${accessory.name} FREE with your subscription.`);
        return;
      }
      
      // Add as free accessory
      const addItem = () => {
        const proceed = window.confirm('Reminder: Fall detection pendants only work when fall detection service is enabled on your Alert1 plan. Do you still want to add this accessory?');
        if (!proceed) {
          return;
        }

        const cartItem: CartItem = {
          id: `accessory-${accessory.id}-subscription`,
          type: 'accessory',
          name: `${accessory.name} (FREE with Subscription)`,
          imageUrl: accessoryImage,
          accessoryId: accessory.id,
          price: 0.00,
          billingCycle: 'one-time'
        };

        cartStorage.addItem(cartItem);
        navigate('/cart');
      };

      runWithSystemConfirmation(addItem, accessory.name);
    } else {
      // Add as pendant-only accessory
      const addItem = () => {
        const proceed = window.confirm('Reminder: Fall detection pendants only work when fall detection service is enabled on your Alert1 plan. Do you still want to add this accessory?');
        if (!proceed) {
          return;
        }

        const cartItem: CartItem = {
          id: `accessory-${accessory.id}-pendant-only`,
          type: 'accessory',
          name: `${accessory.name} (Alert Pendant Only)`,
          imageUrl: accessoryImage,
          accessoryId: accessory.id,
          price: accessory.price ?? 0,
          billingCycle: getBillingCycleFromPriceType(accessory.priceType)
        };

        cartStorage.addItem(cartItem);
        navigate('/cart');
      };

      runWithSystemConfirmation(addItem, accessory.name);
    }
  };

  // Medication Dispenser Features
  const medicationDispenserFeatures = [
    "Program up to four medication reminders per day",
    "Easy to program; extra large 1.5\" LED display",
    "Tamper-proof container",
    "Includes 48-hour rechargeable battery back-up to ensure continuous operation during power failures or while traveling",
    "Audible alarm notifies patient of dosage times (85 dB at 10 cm, 400 Hz)"
  ];

  // Medication Dispenser Additional Features
  const medicationDispenserAdditionalFeatures = [
    "Medication capacity:",
    "Compartments hold up to 9 M&M sized pills each.",
    "Dispenser holds 28 doses, which can be configured as:",
    "4 doses/day for 7 days",
    "3 doses/day for 9 days",
    "2 doses/day for 14 days",
    "1 dose/day for 28 days",
    "If a dose is missed for any reason, the unit denies access to the missed dose.",
    "Compartments keep medications in place even when turned upside-down or shaken.",
    "Integrated card holder for housing physical copy of patient's prescription.",
    "Made in the USA."
  ];

  // Handle medication dispenser order
  const handleMedicationDispenserOrder = () => {
    if (!accessory) return;

    const cartItem: CartItem = {
      id: `accessory-${accessory.id}`,
      type: 'accessory',
      name: accessory.name,
      imageUrl: accessoryImage,
      accessoryId: accessory.id,
      price: accessory.price ?? 0,
      billingCycle: getBillingCycleFromPriceType(accessory.priceType)
    };

    cartStorage.addItem(cartItem);
    navigate('/cart');
  };

  // Handle medical alert bracelet order - two options (free with subscription or button only)
  const handleMedicalAlertBraceletOrder = (option: 'subscription' | 'button-only') => {
    if (!accessory) return;

    if (option === 'subscription') {
      // Check if there's a product in cart
      const existingCartItems = cartStorage.getItems();
      const hasProduct = existingCartItems.some(item => item.type === 'product');
      
      if (!hasProduct) {
        alert(`Please add a medical alert system to your cart first to get the ${accessory.name} FREE with your subscription.`);
        navigate('/cart');
        return;
      }
      
      // Add as free accessory
      const addItem = () => {
        const cartItem: CartItem = {
          id: `accessory-${accessory.id}-subscription`,
          type: 'accessory',
          name: `${accessory.name} (FREE with Subscription)`,
          imageUrl: accessoryImage,
          accessoryId: accessory.id,
          price: 0.00,
          billingCycle: 'one-time'
        };

        cartStorage.addItem(cartItem);
        navigate('/cart');
      };

      runWithSystemConfirmation(addItem, accessory.name);
    } else {
      // Add as $15 button only
      const addItem = () => {
        const cartItem: CartItem = {
          id: `accessory-${accessory.id}-button-only`,
          type: 'accessory',
          name: `${accessory.name} (SOS Button Bracelet Only)`,
          imageUrl: accessoryImage,
          accessoryId: accessory.id,
          price: accessory.price ?? 0,
          billingCycle: getBillingCycleFromPriceType(accessory.priceType)
        };

        cartStorage.addItem(cartItem);
        navigate('/cart');
      };

      runWithSystemConfirmation(addItem, accessory.name);
    }
  };

  // Handle mobile GPS pendant order - two options (free with subscription or wristband only)
  const handleMobileGPSPendantOrder = (option: 'subscription' | 'wristband-only') => {
    if (!accessory) return;

    if (option === 'subscription') {
      // Check if there's a product in cart
      const existingCartItems = cartStorage.getItems();
      const hasProduct = existingCartItems.some(item => item.type === 'product');
      
      if (!hasProduct) {
        alert(`Please add a medical alert system to your cart first to get the ${accessory.name} FREE with your subscription.`);
        return;
      }
      
      // Add as free accessory
      const addItem = () => {
        const cartItem: CartItem = {
          id: `accessory-${accessory.id}-subscription`,
          type: 'accessory',
          name: `${accessory.name} (FREE with Subscription)`,
          imageUrl: accessoryImage,
          accessoryId: accessory.id,
          price: 0.00,
          billingCycle: 'one-time'
        };

        cartStorage.addItem(cartItem);
        navigate('/cart');
      };

      runWithSystemConfirmation(addItem, accessory.name);
    } else {
      // Add as wristband-only accessory
      const addItem = () => {
        const cartItem: CartItem = {
          id: `accessory-${accessory.id}-wristband-only`,
          type: 'accessory',
          name: `${accessory.name} (Wristband Only)`,
          imageUrl: accessoryImage,
          accessoryId: accessory.id,
          price: accessory.price ?? 0,
          billingCycle: getBillingCycleFromPriceType(accessory.priceType)
        };

        cartStorage.addItem(cartItem);
        navigate('/cart');
      };

      runWithSystemConfirmation(addItem, accessory.name);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (requiresSystemConfirmation) {
    return (
      <div className="accessory-page accessory-system-confirmation">
        <div className="confirmation-card">
          <h1>Already Have an Alert1 System?</h1>
          <p>
            The <strong>{accessory.name}</strong> works as an add-on to an existing Alert1 medical alert system.
            Please let us know if you already have a compatible system before continuing.
          </p>
          <div className="confirmation-actions">
            <button
              onClick={confirmSystemAndContinue}
            >
              Yes, I already have a system
            </button>
            <button
              className="secondary"
              onClick={() => {
                cancelSystemConfirmation();
                navigate('/pricing#product-tiles');
              }}
            >
              Show me Alert1 systems first
            </button>
          </div>
          <p className="support-hint">
            Need help? Call us at <a href="tel:1-877-579-9862">1-877-579-9862</a> and we&rsquo;ll make sure you have everything you need.
          </p>
        </div>
      </div>
    );
  }

  const accessoryImageForOg = accessoryImage || (accessory?.imageFilename && getImage(accessory.imageFilename));

  return (
    <div className="accessory-detail-page">
      <OpenGraphHead
        title={accessory.name}
        description={accessory.description ? String(accessory.description).slice(0, 160) : `Shop ${accessory.name} - Alert1 Medical Alert Accessories`}
        imagePath={accessoryImageForOg || '/logo512.png'}
        pathOverride={slug ? `/accessories/${slug}` : undefined}
        imageAlt={accessory.name}
      />
      <section className="accessory-detail-section">
        <div className="container">
          <div className="accessory-detail-grid" style={{ maxWidth: '1200px', margin: '0 auto' }}>
            {/* Left Side - Image Only */}
            <div className="accessory-image-section">
              <div className="accessory-main-image">
                {isPurpleWristband && (getImage('Purple-wristband-5-3.jpg') || accessoryImage) ? (
                  <img 
                    src={getImage('Purple-wristband-5-3.jpg') || accessoryImage} 
                    alt={accessory.name}
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                ) : accessoryImage ? (
                  <img 
                    src={accessoryImage} 
                    alt={accessory.name}
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                ) : getImage('alert1AddedProtectionPlan.jpg') ? (
                  <img 
                    src={getImage('alert1AddedProtectionPlan.jpg')} 
                    alt={accessory.name}
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      display: 'block'
                    }}
                  />
                ) : (
                  <div className="accessory-image-placeholder">
                    <svg width="400" height="400" viewBox="0 0 400 400" fill="none">
                      <rect width="400" height="400" fill="#f0f0f0"/>
                    </svg>
                  </div>
                )}
              </div>
              
              {isProtectionPlan && (
                <div className="accessory-features-list">
                  <ul>
                    {protectionPlanFeatures.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Smoke Detector text moved to separate section below */}

              {/* Pendant text moved to separate section below */}

              {/* Lock Box text moved to separate section below */}

              {/* Fall Detection Pendant content moved to separate sections below */}

              {/* Mobile GPS Pendant content moved to separate section below */}

              {/* Mobile Fall Detection section moved to full-width section below */}

              {/* Medical Alert Bracelet content moved to separate section below */}

              {/* Medication Dispenser content moved to separate sections below */}
            </div>

            {/* Right Side - Details and Purchase */}
            <div className="accessory-info-section">
              <h1 className="accessory-title">{accessory.name}</h1>
              
              {/* Rating and Stock */}
              {(isProtectionPlan || isSmokeDetector || isPendantHelpButton || isLockBox || isFallDetectionPendant || isMobileGPSPendant || isMobileFallDetectionPendant || isMedicalAlertBracelet || isMedicationDispenser || isPurpleWristband) && (
                <div className="accessory-rating">
                  <div className="accessory-stars">
                    {[...Array(5)].map((_, index) => (
                      <svg key={index} width="20" height="20" viewBox="0 0 24 24" fill="#000">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    ))}
                  </div>
                  <span className="accessory-reviews">
                    {isProtectionPlan ? '(12 reviews)' : isSmokeDetector ? '(10 Reviews)' : isLockBox ? '(3 reviews)' : isPurpleWristband ? '(10 reviews)' : '(1 reviews)'} - <span style={{ color: '#28a745' }}>In Stock</span>
                  </span>
                </div>
              )}

              {/* Description for Smoke Detector */}
              {isSmokeDetector && (
                <p style={{ fontSize: '1rem', color: '#666', marginTop: '0.5rem', marginBottom: '1rem' }}>
                  Integrated smoke detection monitoring
                </p>
              )}

              {/* Plan Selector */}
              {isProtectionPlan && (
                <div className="plan-selector">
                  <h3>Select Your Plan</h3>
                  <div className="plan-buttons">
                    <button
                      className={`plan-btn ${paymentPlan === 'monthly' ? 'active' : 'inactive'}`}
                      onClick={() => setPaymentPlan('monthly')}
                    >
                      MONTHLY
                    </button>
                    <button
                      className={`plan-btn ${paymentPlan === 'annual' ? 'active' : 'inactive'}`}
                      onClick={() => setPaymentPlan('annual')}
                    >
                      ANNUALLY
                    </button>
                  </div>
                </div>
              )}

              {/* Price Display - Only for Protection Plan */}
              {isProtectionPlan && (
                <div className="accessory-pricing">
                  <div className="accessory-price">
                    ${getCurrentPrice().toFixed(2)}
                    {paymentPlan === 'annual' ? (
                      <span className="price-note">/year</span>
                    ) : (
                      <span className="price-note">/month</span>
                    )}
                  </div>
                </div>
              )}

              {/* Smoke Detector Pricing Options */}
              {isSmokeDetector && (
                <div style={{ marginTop: '0.5rem' }}>
                  {/* Monitored Service Option */}
                  <div style={{ marginBottom: '0.75rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', background: 'white' }}>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <strong style={{ fontSize: '1.1rem', color: '#333' }}>Monitored Service</strong>
                    </div>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#333', marginBottom: '0.75rem' }}>
                      $5.00<span style={{ fontSize: '1.2rem', fontWeight: 'normal', color: '#666' }}>/month</span>
                    </div>
                    <button 
                      className="order-now-btn"
                      onClick={() => handleSmokeDetectorOrder('monitored')}
                      style={{
                        width: '100%',
                        padding: '0.875rem 1.5rem',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        backgroundColor: '#c8102e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginBottom: '0.5rem'
                      }}
                    >
                      ORDER NOW
                    </button>
                    <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#666', margin: 0 }}>
                      Requires an Alert1 Medical Alert System
                    </p>
                  </div>

                  {/* Detector Only Option */}
                  <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', background: 'white', marginBottom: '1rem' }}>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <strong style={{ fontSize: '1.1rem', color: '#333' }}>Detector Only</strong>
                    </div>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#333', marginBottom: '0.75rem' }}>
                      $30.00<span style={{ fontSize: '1.2rem', fontWeight: 'normal', color: '#666' }}> one-time</span>
                    </div>
                    <button 
                      className="order-now-btn"
                      onClick={() => handleSmokeDetectorOrder('detector-only')}
                      style={{
                        width: '100%',
                        padding: '0.875rem 1.5rem',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        backgroundColor: '#c8102e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginBottom: '0.5rem'
                      }}
                    >
                      ORDER NOW
                    </button>
                    <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#666', margin: 0 }}>
                      No monitoring included
                    </p>
                  </div>

                  {/* Phone Number */}
                  <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                    <a 
                      href="tel:1-877-579-9862" 
                      style={{ 
                        fontSize: '1.5rem', 
                        fontWeight: 'bold', 
                        color: '#333',
                        textDecoration: 'none',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      1-877-579-9862
                    </a>
                  </div>

                  {/* Collapsible Technical Specifications */}
                  <div style={{ marginBottom: '0.75rem', borderBottom: '1px solid #ddd', paddingBottom: '0.75rem' }}>
                    <div
                      onClick={() => toggleSection('technical')}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      <span>Technical Specifications</span>
                      <span>{expandedSections.technical ? '−' : '+'}</span>
                    </div>
                    {expandedSections.technical && (
                      <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', lineHeight: '1.8', marginTop: '1rem' }}>
                        <li>Signals alerts through EZ Care or EZ Care Plus</li>
                        <li>Beeps to indicate when battery is low</li>
                        <li>Added life- and home-saving protection</li>
                        <li>Wireless for easy and flexible installation</li>
                      </ul>
                    )}
                  </div>

                  {/* Collapsible Shipping Details */}
                  <div style={{ marginBottom: '0.75rem', borderBottom: '1px solid #ddd', paddingBottom: '0.75rem' }}>
                    <div
                      onClick={() => toggleSection('shipping')}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      <span>Shipping Details</span>
                      <span>{expandedSections.shipping ? '−' : '+'}</span>
                    </div>
                    {expandedSections.shipping && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <p style={{ marginBottom: '0.5rem' }}><strong>Delivery:</strong> Free Shipping</p>
                        <p><strong>Setup:</strong> Easy self-installation.</p>
                      </div>
                    )}
                  </div>

                  {/* Collapsible Refund Policy */}
                  <div style={{ marginBottom: '0.75rem', borderBottom: '1px solid #ddd', paddingBottom: '0.75rem' }}>
                    <div
                      onClick={() => toggleSection('refund')}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      <span>Refund Policy</span>
                      <span>{expandedSections.refund ? '−' : '+'}</span>
                    </div>
                    {expandedSections.refund && (
                      <p style={{ marginTop: '0.75rem' }}>Alert1 does not require long term contracts. Any user may cancel service at any time, simply by returning the Alert1 equipment. However, some users agree to contracts of a specified length (for example, annual plans) in exchange for a substantial discount. Such users may cancel their service early, but Alert1 does not offer refunds in such circumstances unless the user has passed away or moved into a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Pendant Pricing Options */}
              {isPendantHelpButton && (
                <div style={{ marginTop: '2rem' }}>
                  {/* FREE with Subscription Option */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#c8102e', marginBottom: '0.5rem' }}>
                      FREE
                    </div>
                    <div style={{ fontSize: '1rem', color: '#666', marginBottom: '1rem' }}>
                      with Alert1 emergency monitoring service
                    </div>
                    <div style={{ fontSize: '1rem', color: '#666', marginBottom: '1rem' }}>
                      {accessory?.price !== undefined ? `${formatCurrency(accessory.price)} ${getPriceFrequencyLabel(accessory.priceType)}` : ''}
                    </div>

                    {/* Adding additional members */}
                    <div style={{ marginBottom: '1rem', fontSize: '0.95rem' }}>
                      <strong>Adding additional members?</strong>
                    </div>
                    <p style={{ marginBottom: '1rem', fontSize: '0.95rem' }}>
                      Call us today at <a href="tel:1-877-579-9862" style={{ color: '#0066cc', textDecoration: 'underline', fontWeight: 'bold' }}>1-877-579-9862</a>
                    </p>

                    {/* Action Buttons */}
                    <button
                      onClick={() => handlePendantOrder('subscription')}
                      style={{
                        width: '100%',
                        padding: '1rem 2rem',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        backgroundColor: '#c8102e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginBottom: '1rem'
                      }}
                    >
                      Add to Alert1 Subscription
                    </button>

                    <button
                      onClick={() => handlePendantOrder('button-only')}
                      style={{
                        width: '100%',
                        padding: '1rem 2rem',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginBottom: '1.5rem'
                      }}
                    >
                      Add Button Only
                    </button>

                    {/* Collapsible Technical Specifications */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('technical')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Technical Specifications</span>
                        <span>{expandedSections.technical ? '−' : '+'}</span>
                      </div>
                      {expandedSections.technical && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', lineHeight: '1.8' }}>
                            <li><strong>Weight:</strong> 0.5oz</li>
                            <li>Water-Resistant</li>
                            <li>Range of up to 400' of open air</li>
                            <li>2-3 Year Battery Life</li>
                            <li>Low battery alert sent to central command when button is pressed</li>
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Collapsible Shipping Details */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('shipping')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Shipping Details</span>
                        <span>{expandedSections.shipping ? '−' : '+'}</span>
                      </div>
                      {expandedSections.shipping && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <p><strong>Delivery:</strong> Free Shipping</p>
                          <p><strong>Setup:</strong> Easy self-installation.</p>
                        </div>
                      )}
                    </div>

                    {/* Collapsible Refund Policy */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('refund')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Refund Policy</span>
                        <span>{expandedSections.refund ? '−' : '+'}</span>
                      </div>
                      {expandedSections.refund && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <p>Alert1 does not require long term contracts. Any user may cancel service at any time, simply by returning the Alert1 equipment. However, some users agree to contracts of a specified length (for example, annual plans) in exchange for a substantial discount. Such users may cancel their service early, but Alert1 does not offer refunds in such circumstances unless the user has passed away or moved into a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Lock Box Pricing */}
              {isLockBox && (
                <div style={{ marginTop: '2rem' }}>
                  {/* Price Display */}
                  <div style={{ marginBottom: '2rem' }}>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#333', marginBottom: '1rem' }}>
                      $25.00
                    </div>
                    <button
                      onClick={handleLockBoxOrder}
                      style={{
                        width: '100%',
                        padding: '1rem 2rem',
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        backgroundColor: '#c8102e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginBottom: '1rem'
                      }}
                    >
                      ORDER NOW
                    </button>
                    <p style={{ textAlign: 'center', fontSize: '1rem', margin: 0 }}>
                      or <a href="tel:1-877-579-9862" style={{ color: '#28a745', textDecoration: 'underline', fontWeight: 'bold' }}>call 1-877-579-9862</a>
                    </p>
                  </div>

                  {/* Collapsible Technical Specifications */}
                  <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                    <div
                      onClick={() => toggleSection('technical')}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      <span>Technical Specifications</span>
                      <span>{expandedSections.technical ? '−' : '+'}</span>
                    </div>
                    {expandedSections.technical && (
                      <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                        <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', lineHeight: '1.8' }}>
                          <li><strong>Dimension:</strong> 7.5" x 2" x 1"</li>
                          <li><strong>Weight:</strong> 1lb 15oz.</li>
                          <li>Solid, weather-resistant metal</li>
                          <li>Easy to use designer key box</li>
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Collapsible Shipping Details */}
                  <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                    <div
                      onClick={() => toggleSection('shipping')}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      <span>Shipping Details</span>
                      <span>{expandedSections.shipping ? '−' : '+'}</span>
                    </div>
                    {expandedSections.shipping && (
                      <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                        <p><strong>Delivery:</strong> Free Shipping</p>
                        <p><strong>Setup:</strong> Easy self-installation.</p>
                      </div>
                    )}
                  </div>

                  {/* Collapsible Refund Policy */}
                  <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                    <div
                      onClick={() => toggleSection('refund')}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      <span>Refund Policy</span>
                      <span>{expandedSections.refund ? '−' : '+'}</span>
                    </div>
                    {expandedSections.refund && (
                      <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                        <p>Alert1 does not require long term contracts. Any user may cancel service at any time, simply by returning the Alert1 equipment. However, some users agree to contracts of a specified length (for example, annual plans) in exchange for a substantial discount. Such users may cancel their service early, but Alert1 does not offer refunds in such circumstances unless the user has passed away or moved into a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Fall Detection Pendant Pricing Options */}
              {isFallDetectionPendant && (
                <div style={{ marginTop: '2rem' }}>
                  {/* FREE with Subscription Option */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#c8102e', marginBottom: '0.5rem' }}>
                      FREE
                    </div>
                    <div style={{ fontSize: '1rem', color: '#666', marginBottom: '1rem' }}>
                      with Alert1 emergency monitoring service
                    </div>
                    <div style={{ fontSize: '1rem', color: '#666', marginBottom: '1rem' }}>
                      {accessory?.price !== undefined ? `${formatCurrency(accessory.price)} ${getPriceFrequencyLabel(accessory.priceType)}` : ''}
                    </div>

                    {/* Adding additional members */}
                    <div style={{ marginBottom: '1rem', fontSize: '0.95rem' }}>
                      <strong>Adding additional members?</strong>
                    </div>
                    <p style={{ marginBottom: '1rem', fontSize: '0.95rem' }}>
                      Call us today at <a href="tel:1-877-579-9862" style={{ color: '#0066cc', textDecoration: 'underline', fontWeight: 'bold' }}>1-877-579-9862</a>
                    </p>

                    {/* Action Buttons */}
                    <button
                      onClick={() => handleFallDetectionPendantOrder('subscription')}
                      style={{
                        width: '100%',
                        padding: '1rem 2rem',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        backgroundColor: '#c8102e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginBottom: '1rem'
                      }}
                    >
                      Add to Alert1 Subscription
                    </button>

                    <button
                      onClick={() => handleFallDetectionPendantOrder('button-only')}
                      style={{
                        width: '100%',
                        padding: '1rem 2rem',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginBottom: '1.5rem'
                      }}
                    >
                      Add Button Only
                    </button>

                    {/* Collapsible Technical Specifications */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('technical')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Technical Specifications</span>
                        <span>{expandedSections.technical ? '−' : '+'}</span>
                      </div>
                      {expandedSections.technical && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', lineHeight: '1.8' }}>
                            <li><strong>Dimension:</strong> 2.25" x 1.5" x .5"</li>
                            <li><strong>Weight:</strong> 1.6oz.</li>
                            <li>Activates alarm call when a fall is detected</li>
                            <li>IP67 waterproof for use in shower or bath</li>
                            <li>Manual activation by pressing button</li>
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Collapsible Shipping Details */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('shipping')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Shipping Details</span>
                        <span>{expandedSections.shipping ? '−' : '+'}</span>
                      </div>
                      {expandedSections.shipping && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <p><strong>Delivery:</strong> Free Shipping</p>
                          <p><strong>Setup:</strong> Easy self-installation.</p>
                        </div>
                      )}
                    </div>

                    {/* Collapsible Refund Policy */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('refund')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Refund Policy</span>
                        <span>{expandedSections.refund ? '−' : '+'}</span>
                      </div>
                      {expandedSections.refund && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <p>Alert1 does not require long term contracts. Any user may cancel service at any time, simply by returning the Alert1 equipment. However, some users agree to contracts of a specified length (for example, annual plans) in exchange for a substantial discount. Such users may cancel their service early, but Alert1 does not offer refunds in such circumstances unless the user has passed away or moved into a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Mobile GPS Pendant Pricing Options */}
              {isMobileGPSPendant && (
                <div style={{ marginTop: '2rem' }}>
                  {/* FREE with Subscription Option */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#c8102e', marginBottom: '0.5rem' }}>
                      FREE
                    </div>
                    <div style={{ fontSize: '1rem', color: '#666', marginBottom: '1rem' }}>
                      with Alert1 emergency monitoring service
                    </div>
                    <div style={{ fontSize: '1rem', color: '#666', marginBottom: '1rem' }}>
                      {accessory?.price !== undefined ? `${formatCurrency(accessory.price)} ${getPriceFrequencyLabel(accessory.priceType)}` : ''}
                    </div>

                    {/* Adding additional members */}
                    <div style={{ marginBottom: '1rem', fontSize: '0.95rem' }}>
                      <strong>Adding additional members?</strong>
                    </div>
                    <p style={{ marginBottom: '1rem', fontSize: '0.95rem' }}>
                      Call us today at <a href="tel:1-877-579-9862" style={{ color: '#0066cc', textDecoration: 'underline', fontWeight: 'bold' }}>1-877-579-9862</a>
                    </p>

                    {/* Action Buttons */}
                    <button
                      onClick={() => handleMobileGPSPendantOrder('subscription')}
                      style={{
                        width: '100%',
                        padding: '1rem 2rem',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        backgroundColor: '#c8102e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginBottom: isNoStandaloneOrder ? '1.5rem' : '1rem'
                      }}
                    >
                      Add to Alert1 Subscription
                    </button>

                    {!isNoStandaloneOrder && (
                    <button
                      onClick={() => handleMobileGPSPendantOrder('wristband-only')}
                      style={{
                        width: '100%',
                        padding: '1rem 2rem',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginBottom: '1.5rem'
                      }}
                    >
                      Add Wristband Only
                    </button>
                    )}

                    {/* Collapsible Technical Specifications */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('technical')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Technical Specifications</span>
                        <span>{expandedSections.technical ? '−' : '+'}</span>
                      </div>
                      {expandedSections.technical && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', lineHeight: '1.8' }}>
                            <li><strong>Dimension:</strong> 2.4" x 1.6" x .6"</li>
                            <li><strong>Weight:</strong> 1.3 oz</li>
                            <li><strong>Highly water resistant (IP67):</strong> designed to be worn in shower</li>
                            <li>Pairable with a smoke detector</li>
                            <li>Battery will last up to 10 days</li>
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Collapsible Shipping Details */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('shipping')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Shipping Details</span>
                        <span>{expandedSections.shipping ? '−' : '+'}</span>
                      </div>
                      {expandedSections.shipping && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <p><strong>Delivery:</strong> Free shipping</p>
                          <p><strong>Setup:</strong> Comes pre-programmed. Simply charge if needed, send a test alert, and the device is ready to use.</p>
                        </div>
                      )}
                    </div>

                    {/* Collapsible Refund Policy */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('refund')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Refund Policy</span>
                        <span>{expandedSections.refund ? '−' : '+'}</span>
                      </div>
                      {expandedSections.refund && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <p>Alert1 does not require long term contracts. Any user may cancel service at any time, simply by returning the Alert1 equipment. However, some users agree to contracts of a specified length (for example, annual plans) in exchange for a substantial discount. Such users may cancel their service early, but Alert1 does not offer refunds in such circumstances unless the user has passed away or moved into a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Mobile Fall Detection Pendant Pricing Options */}
              {isMobileFallDetectionPendant && (
                <div style={{ marginTop: '2rem' }}>
                  {/* FREE with Subscription Option */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#c8102e', marginBottom: '0.5rem' }}>
                      FREE
                    </div>
                    <div style={{ fontSize: '1rem', color: '#666', marginBottom: '1rem' }}>
                      with Alert1 emergency monitoring service
                    </div>
                    <div style={{ fontSize: '1rem', color: '#666', marginBottom: '1rem' }}>
                      {accessory?.price !== undefined ? `${formatCurrency(accessory.price)} ${getPriceFrequencyLabel(accessory.priceType)}` : ''}
                    </div>

                    {/* Adding additional members */}
                    <div style={{ marginBottom: '1rem', fontSize: '0.95rem' }}>
                      <strong>Adding additional members?</strong>
                    </div>
                    <p style={{ marginBottom: '1rem', fontSize: '0.95rem' }}>
                      Call us today at <a href="tel:1-877-579-9862" style={{ color: '#0066cc', textDecoration: 'underline', fontWeight: 'bold' }}>1-877-579-9862</a>
                    </p>

                    {/* Action Buttons */}
                    <button
                      onClick={() => handleMobileFallDetectionPendantOrder('subscription')}
                      style={{
                        width: '100%',
                        padding: '1rem 2rem',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        backgroundColor: '#c8102e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginBottom: isNoStandaloneOrder ? '1.5rem' : '1rem'
                      }}
                    >
                      Add to Alert1 Subscription
                    </button>

                    {!isNoStandaloneOrder && (
                    <button
                      onClick={() => handleMobileFallDetectionPendantOrder('pendant-only')}
                      style={{
                        width: '100%',
                        padding: '1rem 2rem',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginBottom: '1.5rem'
                      }}
                    >
                      Add Pendant Only
                    </button>
                    )}

                    {/* Collapsible Technical Specifications */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('technical')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Technical Specifications</span>
                        <span>{expandedSections.technical ? '−' : '+'}</span>
                      </div>
                      {expandedSections.technical && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', lineHeight: '1.8' }}>
                            <li>Dimensions: 2.72"H x 1.38"W x .57"D; 1.3 Ounces</li>
                            <li>Highly water resistant (IP67); designed to be worn in shower</li>
                            <li>Humidity: Maximum non-condensing humidity 95% RH @ 50°C</li>
                            <li>Functional at temperatures from -4°F to 131°F (-20°C to 55°C)</li>
                            <li>Storage temperature range: -40° F to 185° F (-40° C to 85° C)</li>
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Collapsible Shipping Details */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('shipping')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Shipping Details</span>
                        <span>{expandedSections.shipping ? '−' : '+'}</span>
                      </div>
                      {expandedSections.shipping && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <p><strong>Delivery:</strong> Free Shipping</p>
                          <p><strong>Setup:</strong> Easy self-installation.</p>
                        </div>
                      )}
                    </div>

                    {/* Collapsible Refund Policy */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('refund')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Refund Policy</span>
                        <span>{expandedSections.refund ? '−' : '+'}</span>
                      </div>
                      {expandedSections.refund && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <p>Alert1 does not require long term contracts. Any user may cancel service at any time, simply by returning the Alert1 equipment. However, some users agree to contracts of a specified length (for example, annual plans) in exchange for a substantial discount. Such users may cancel their service early, but Alert1 does not offer refunds in such circumstances unless the user has passed away or moved into a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Medical Alert Bracelet Pricing Options */}
              {isMedicalAlertBracelet && (
                <div style={{ marginTop: '2rem' }}>
                  {/* FREE with Subscription Option */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#c8102e', marginBottom: '0.5rem' }}>
                      FREE
                    </div>
                    <div style={{ fontSize: '1rem', color: '#666', marginBottom: '1rem' }}>
                      with Alert1 emergency monitoring service
                    </div>
                    <div style={{ fontSize: '1rem', color: '#666', marginBottom: '1rem' }}>
                      {accessory?.price !== undefined ? `${formatCurrency(accessory.price)} ${getPriceFrequencyLabel(accessory.priceType)}` : ''}
                    </div>

                    {/* Adding additional members */}
                    <div style={{ marginBottom: '1rem', fontSize: '0.95rem' }}>
                      <strong>Adding additional members?</strong>
                    </div>
                    <p style={{ marginBottom: '1rem', fontSize: '0.95rem' }}>
                      Call us today at <a href="tel:1-877-579-9862" style={{ color: '#0066cc', textDecoration: 'underline', fontWeight: 'bold' }}>1-877-579-9862</a>
                    </p>

                    {/* Action Buttons */}
                    <button
                      onClick={() => handleMedicalAlertBraceletOrder('subscription')}
                      style={{
                        width: '100%',
                        padding: '1rem 2rem',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        backgroundColor: '#c8102e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginBottom: '1rem'
                      }}
                    >
                      Add to Alert1 Subscription
                    </button>

                    <button
                      onClick={() => handleMedicalAlertBraceletOrder('button-only')}
                      style={{
                        width: '100%',
                        padding: '1rem 2rem',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginBottom: '1.5rem'
                      }}
                    >
                      Add Button Only
                    </button>

                    {/* Collapsible Technical Specifications */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('technical')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Technical Specifications</span>
                        <span>{expandedSections.technical ? '−' : '+'}</span>
                      </div>
                      {expandedSections.technical && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', lineHeight: '1.8' }}>
                            <li><strong>Weight:</strong> 0.5oz</li>
                            <li>Water-Resistant</li>
                            <li>Range of up to 400' of open air</li>
                            <li>2-3 Year Battery Life</li>
                            <li>Low battery alert sent to central command when button is pressed</li>
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Collapsible Shipping Details */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('shipping')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Shipping Details</span>
                        <span>{expandedSections.shipping ? '−' : '+'}</span>
                      </div>
                      {expandedSections.shipping && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <p><strong>Delivery:</strong> Free Shipping</p>
                          <p><strong>Setup:</strong> Easy self-installation</p>
                        </div>
                      )}
                    </div>

                    {/* Collapsible Refund Policy */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('refund')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Refund Policy</span>
                        <span>{expandedSections.refund ? '−' : '+'}</span>
                      </div>
                      {expandedSections.refund && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <p>Alert1 does not require long term contracts. Any user may cancel service at any time, simply by returning the Alert1 equipment. However, some users agree to contracts of a specified length (for example, annual plans) in exchange for a substantial discount. Such users may cancel their service early, but Alert1 does not offer refunds in such circumstances unless the user has passed away or moved into a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Medication Dispenser Pricing */}
              {isMedicationDispenser && (
                <div style={{ marginTop: '2rem' }}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#333', marginBottom: '1rem' }}>
                      $149.95
                    </div>
                    
                    <button
                      onClick={handleMedicationDispenserOrder}
                      style={{
                        width: '100%',
                        padding: '1rem 2rem',
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        backgroundColor: '#c8102e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginBottom: '1rem'
                      }}
                    >
                      ORDER NOW
                    </button>

                    <p style={{ textAlign: 'center', marginBottom: '1rem', fontSize: '1rem' }}>
                      or <a href="tel:1-877-579-9862" style={{ color: '#28a745', textDecoration: 'underline', fontWeight: 'bold' }}>call 1-877-579-9862</a>
                    </p>

                    {/* Collapsible Technical Specifications */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('technical')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Technical Specifications</span>
                        <span>{expandedSections.technical ? '−' : '+'}</span>
                      </div>
                      {expandedSections.technical && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', lineHeight: '1.8' }}>
                            <li><strong>Diameter:</strong> 11.5"</li>
                            <li><strong>Height:</strong> 3"</li>
                            <li><strong>Weight:</strong> 1.4 lbs.</li>
                            <li><strong>Pill compartments:</strong> 1" x ½" x ¾"</li>
                            <li>Made in USA</li>
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Collapsible Shipping Details */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('shipping')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Shipping Details</span>
                        <span>{expandedSections.shipping ? '−' : '+'}</span>
                      </div>
                      {expandedSections.shipping && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <p><strong>Delivery:</strong> Free Shipping</p>
                          <p><strong>Setup:</strong> Easy self-installation</p>
                        </div>
                      )}
                    </div>

                    {/* Collapsible Refund Policy */}
                    <div style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '1rem' }}>
                      <div
                        onClick={() => toggleSection('refund')}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        <span>Refund Policy</span>
                        <span>{expandedSections.refund ? '−' : '+'}</span>
                      </div>
                      {expandedSections.refund && (
                        <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                          <p>Alert1 does not require long term contracts. Any user may cancel service at any time, simply by returning the Alert1 equipment. However, some users agree to contracts of a specified length (for example, annual plans) in exchange for a substantial discount. Such users may cancel their service early, but Alert1 does not offer refunds in such circumstances unless the user has passed away or moved into a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Description - Show for non-protection-plan, non-smoke-detector, non-pendant, non-lockbox, non-fall-detection, non-mobile-gps, non-mobile-fall-detection, non-medical-alert-bracelet, non-medication-dispenser, non-purple-wristband accessories */}
              {!isProtectionPlan && !isSmokeDetector && !isPendantHelpButton && !isLockBox && !isFallDetectionPendant && !isMobileGPSPendant && !isMobileFallDetectionPendant && !isMedicalAlertBracelet && !isMedicationDispenser && !isPurpleWristband && accessory.description && (
                <div className="accessory-description">
                  <p>{accessory.description}</p>
                </div>
              )}

              {/* Price and Order Button for Purple Wristband */}
              {isPurpleWristband && (
                <div style={{ marginTop: '1rem', marginBottom: '1.5rem' }}>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#333', marginBottom: '1rem' }}>
                    ${accessory.price?.toFixed(2) || '9.95'}
                  </div>
                  <button 
                    className="order-now-btn"
                    onClick={() => {
                      if (!accessory) return;
                      const cartItem: CartItem = {
                        id: `accessory-${accessory.id}`,
                        type: 'accessory',
                        name: accessory.name,
                        imageUrl: accessoryImage,
                        accessoryId: accessory.id,
                        price: accessory.price ?? 9.95,
                        billingCycle: 'one-time'
                      };
                      cartStorage.addItem(cartItem);
                      navigate('/cart');
                    }}
                    style={{
                      width: '100%',
                      padding: '1rem 2rem',
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      backgroundColor: '#c8102e',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      marginBottom: '1rem'
                    }}
                  >
                    Order Now
                  </button>
                  <p style={{ textAlign: 'center', fontSize: '1.1rem', marginTop: '0.5rem' }}>
                    or <a href="tel:1-877-579-9862" style={{ color: '#0066cc', textDecoration: 'underline', fontWeight: 'bold' }}>call 1-877-579-9862</a>
                  </p>

                  {/* Collapsible Technical Specifications */}
                  <div style={{ marginTop: '2rem', marginBottom: '0.75rem', borderBottom: '1px solid #ddd', paddingBottom: '0.75rem' }}>
                    <div
                      onClick={() => toggleSection('technical')}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      <span>Technical Specifications</span>
                      <span>{expandedSections.technical ? '−' : '+'}</span>
                    </div>
                    {expandedSections.technical && (
                      <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', lineHeight: '1.8', marginTop: '1rem' }}>
                        <li>Soft, lightweight silicone material</li>
                        <li>Waterproof</li>
                        <li>Length: 10.5&quot;, width: 0.9&quot;</li>
                      </ul>
                    )}
                  </div>

                  {/* Collapsible Shipping Details */}
                  <div style={{ marginBottom: '0.75rem', borderBottom: '1px solid #ddd', paddingBottom: '0.75rem' }}>
                    <div
                      onClick={() => toggleSection('shipping')}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      <span>Shipping Details</span>
                      <span>{expandedSections.shipping ? '−' : '+'}</span>
                    </div>
                    {expandedSections.shipping && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <p style={{ marginBottom: '0.5rem' }}><strong>Delivery:</strong> Your package will arrive in 2-3 business days with express shipping</p>
                        <p><strong>Setup:</strong> Easy self-installation</p>
                      </div>
                    )}
                  </div>

                  {/* Collapsible Refund Policy */}
                  <div style={{ marginBottom: '0.75rem', borderBottom: '1px solid #ddd', paddingBottom: '0.75rem' }}>
                    <div
                      onClick={() => toggleSection('refund')}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      <span>Refund Policy</span>
                      <span>{expandedSections.refund ? '−' : '+'}</span>
                    </div>
                    {expandedSections.refund && (
                      <p style={{ marginTop: '0.75rem' }}>30 day returns accepted if item is unused and in original packaging. Purchaser is responsible for return shipping fees.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Order Button - Only for Protection Plan */}
              {isProtectionPlan && (
                <>
                  <button 
                    className="order-now-btn"
                    onClick={handleOrderNow}
                    style={{
                      width: '100%',
                      padding: '1rem 2rem',
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      backgroundColor: '#c8102e',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      marginTop: '1.5rem',
                      marginBottom: '1rem'
                    }}
                  >
                    ORDER NOW
                  </button>

                  {/* Phone Number */}
                  <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '1.25rem', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    or call <a href="tel:18775799862" style={{ color: '#28a745', textDecoration: 'underline', fontSize: '1.25rem', fontWeight: 'bold' }}>1-877-579-9862</a>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Content Section - Protect Your Home from Fires - Smoke Detector */}
      {isSmokeDetector && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
            <div>
              {(accessoryImage || 
                getImage('accessories-Smoke-Detector-1—3.jpg') || 
                getImage('accessories-Smoke-Detector-1-3.jpg') ||
                getImage('accessories-Smoke-Detector-1—3.JPG') ||
                getImage('accessories-Smoke-Detector-1-3.JPG')) && (
                <img 
                  src={accessoryImage || 
                       getImage('accessories-Smoke-Detector-1—3.jpg') || 
                       getImage('accessories-Smoke-Detector-1-3.jpg') ||
                       getImage('accessories-Smoke-Detector-1—3.JPG') ||
                       getImage('accessories-Smoke-Detector-1-3.JPG')} 
                  alt="Smoke Detector" 
                  style={{ width: '100%', maxWidth: '600px', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Protect Your Home from Fires
              </h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {smokeDetectorFeatures.map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '0.75rem', lineHeight: '1.6' }}>{feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Additional Accessories Section - Only for Smoke Detector */}
      {isSmokeDetector && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Additional Accessories for Your Home or Mobile Medical Alert System
              </h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-8" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Medical Alert Bracelet - Emergency Alarm Wristband
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/added-protection-plan" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Added Protection Plan
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/medication-reminder-organizer" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Medication Reminder
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-4" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Emergency Lock Box
                  </a>
                </li>
              </ul>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {getImage('help-button-6.png') && (
                <img 
                  src={getImage('help-button-6.png')} 
                  alt="Help Button" 
                  style={{ maxWidth: '400px', width: '100%', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
          </div>
        </section>
      )}

      {/* Content Section - Push the Button Get Help Fast - Pendant */}
      {isPendantHelpButton && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
            <div>
              {getImage('in-home-pendant-alert1.png') && (
                <img 
                  src={getImage('in-home-pendant-alert1.png')} 
                  alt="In-home Pendant" 
                  style={{ width: '100%', maxWidth: '600px', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Push the Button, Get Help Fast 24/7
              </h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {pendantFeatures.map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '0.75rem', lineHeight: '1.6' }}>{feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Additional Accessories Section - For Pendant */}
      {isPendantHelpButton && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Additional Accessories for Your Home or Mobile Medical Alert System
              </h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-4" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Alert1 Emergency Lock Box
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-8" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Emergency Alarm Wristband
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-1" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Added Protection Plan
                  </a>
                </li>
              </ul>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', flexWrap: 'wrap' }}>
              {getImage('wrist-alarm-2.png') && (
                <img 
                  src={getImage('wrist-alarm-2.png')} 
                  alt="Wrist Alarm" 
                  style={{ maxWidth: '600px', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
          </div>
        </section>
      )}

      {/* Content Section - Protect Your Home - Lock Box */}
      {isLockBox && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
            <div>
              {getImage('Alert1-Emergency-Lock-Box-2.png') && (
                <img 
                  src={getImage('Alert1-Emergency-Lock-Box-2.png')} 
                  alt="Emergency Lock Box" 
                  style={{ width: '100%', maxWidth: '600px', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Protect Your Home While Allowing Help to Enter
              </h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {lockBoxFeatures.map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '0.75rem', lineHeight: '1.6' }}>{feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Additional Accessories Section - For Lock Box */}
      {isLockBox && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Additional Accessories for Your Home or Mobile Medical Alert System
              </h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-2" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Monitored Smoke Detector
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-9" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Automatic Medication Dispenser & Organizer
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-1" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Added Protection Plan
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-8" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Medical Alert Bracelet - Emergency Alarm Wristband
                  </a>
                </li>
              </ul>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
              {getImage('accessories-Smoke-Detector-1-.jpg') && (
                <img 
                  src={getImage('accessories-Smoke-Detector-1-.jpg')} 
                  alt="Smoke Detector" 
                  style={{ maxWidth: '600px', width: '100%', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
          </div>
        </section>
      )}

      {/* Content Section for Mobile Fall Detection - Image left, text right */}
      {isMobileFallDetectionPendant && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
            <div>
              {getImage('2-2.jpg') && (
                <img 
                  src={getImage('2-2.jpg')} 
                  alt="Mobile Fall Detection" 
                  style={{ width: '100%', maxWidth: '500px', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                24/7 Automatic Fall Detection Alert Button works Anywhere in the US
              </h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {mobileFallDetectionFeatures.map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '0.5rem', lineHeight: '1.6' }}>{feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Additional Accessories Section - For Mobile Fall Detection Pendant */}
      {isMobileFallDetectionPendant && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'start' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
              {getImage('help-button-6.png') && (
                <img 
                  src={getImage('help-button-6.png')} 
                  alt="Help Button" 
                  style={{ maxWidth: '400px', width: '100%', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Additional Accessories for Your Home or Mobile Medical Alert System
              </h2>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', margin: 0 }}>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-8" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Medical Alert Bracelet - Emergency Alarm Wristband
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-9" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Automatic Medication Dispenser & Organizer
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-4" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Alert1 Emergency Lock Box
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Content Section - Never Miss a Dose - Medication Dispenser */}
      {isMedicationDispenser && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'start' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
              {(getImage('medication-dispenser-2-3.jpeg') || getImage('medication-dispenser-2-4.jpeg') || accessoryImage) && (
                <img 
                  src={getImage('medication-dispenser-2-3.jpeg') || getImage('medication-dispenser-2-4.jpeg') || accessoryImage} 
                  alt="Medication Dispenser" 
                  style={{ width: '100%', maxWidth: '600px', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Never Miss a Dose with our Medication Dispenser, Reminder, and Organizer
              </h2>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', margin: 0 }}>
                {medicationDispenserFeatures.map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '0.75rem', lineHeight: '1.6' }}>{feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Content Section - Additional Features - Medication Dispenser */}
      {isMedicationDispenser && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'start' }}>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Additional Features
              </h2>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', margin: 0 }}>
                {medicationDispenserAdditionalFeatures.map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '0.5rem', lineHeight: '1.6' }}>{feature}</li>
                ))}
              </ul>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
              {(getImage('medication-dispenser-2-4.jpeg') || getImage('medication-dispenser-2-3.jpeg') || accessoryImage) && (
                <img 
                  src={getImage('medication-dispenser-2-4.jpeg') || getImage('medication-dispenser-2-3.jpeg') || accessoryImage} 
                  alt="Medication Dispenser" 
                  style={{ width: '100%', maxWidth: '600px', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
          </div>
        </section>
      )}

      {/* Additional Accessories Section - For Medication Dispenser */}
      {isMedicationDispenser && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'start' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
              {getImage('help-button-6.png') && (
                <img 
                  src={getImage('help-button-6.png')} 
                  alt="Help Button" 
                  style={{ maxWidth: '400px', width: '100%', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Additional Accessories for Your Home or Mobile Medical Alert System
              </h2>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', margin: 0 }}>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-2" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Monitored Smoke Detector
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-1" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Added Protection Plan
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-4" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Alert1 Emergency Lock Box
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-8" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Medical Alert Bracelet - Emergency Alarm Wristband
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Content Section - Convenient 24/7 Access to Help - Medical Alert Bracelet */}
      {isMedicalAlertBracelet && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'start' }}>
            <div>
              {(getImage('wrist-alarm-2.png') || accessoryImage) && (
                <img 
                  src={getImage('wrist-alarm-2.png') || accessoryImage} 
                  alt="Medical Alert Bracelet" 
                  style={{ width: '100%', maxWidth: '600px', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Convenient 24/7 Access to Help
              </h2>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', margin: 0 }}>
                {medicalAlertBraceletFeatures.map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '0.75rem', lineHeight: '1.6' }}>{feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Additional Accessories Section - For Medical Alert Bracelet */}
      {isMedicalAlertBracelet && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'start' }}>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Additional Accessories for Your Home or Mobile Medical Alert System
              </h2>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', margin: 0 }}>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-1" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Added Protection Plan
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-9" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Medication Reminder
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-4" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Emergency Lock Box
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-2" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Monitored Smoke Detector
                  </a>
                </li>
              </ul>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
              {getImage('help-button-6.png') && (
                <img 
                  src={getImage('help-button-6.png')} 
                  alt="Help Button" 
                  style={{ maxWidth: '300px', width: '100%', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
          </div>
        </section>
      )}

      {/* Content Section - Wristband Button Alert - Mobile GPS Pendant */}
      {isMobileGPSPendant && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'start' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
              {(getImage('lifebeacon-on-the-go-6-1-.jpg') || accessoryImage) && (
                <img 
                  src={getImage('lifebeacon-on-the-go-6-1-.jpg') || accessoryImage} 
                  alt="Mobile GPS Pendant" 
                  style={{ width: '100%', maxWidth: '600px', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Wristband Button Alert Gives 24/7 Access to Help
              </h2>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', margin: 0 }}>
                {mobileGPSFeatures.map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '0.75rem', lineHeight: '1.6', color: '#333' }}>{feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Additional Accessories Section - For Mobile GPS Pendant */}
      {isMobileGPSPendant && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'start' }}>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Additional Accessories for Your Home or Mobile Medical Alert System
              </h2>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', margin: 0 }}>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-4" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Emergency Lock Box
                  </a>
                </li>
              </ul>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
              {getImage('help-button-6.png') && (
                <img 
                  src={getImage('help-button-6.png')} 
                  alt="Help Button" 
                  style={{ maxWidth: '400px', width: '100%', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
          </div>
        </section>
      )}

      {/* Content Section 1 - Fall Detection Pendant */}
      {isFallDetectionPendant && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
            <div>
              {getImage('InTheHomeFallDetectionMedicalAlert.jpg') && (
                <img 
                  src={getImage('InTheHomeFallDetectionMedicalAlert.jpg')} 
                  alt="Fall Detection" 
                  style={{ width: '100%', maxWidth: '600px', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Fall detection alert pendant for Alert1 in-home medical alarm system
              </h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {fallDetectionFeatures1.map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '0.75rem', lineHeight: '1.6' }}>{feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Content Section 2 - Automatic Fall Detection - Fall Detection Pendant */}
      {isFallDetectionPendant && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Automatic fall detection for the home
              </h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {fallDetectionFeatures2.map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '0.75rem', lineHeight: '1.6' }}>{feature}</li>
                ))}
              </ul>
            </div>
            <div>
              {getImage('medical-alert-system-alert1-falldetection.jpg') && (
                <img 
                  src={getImage('medical-alert-system-alert1-falldetection.jpg')} 
                  alt="Fall Detection System" 
                  style={{ width: '100%', maxWidth: '600px', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
          </div>
        </section>
      )}

      {/* Additional Accessories Section - For Fall Detection Pendant */}
      {isFallDetectionPendant && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'start' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
              {getImage('help-button-6.png') && (
                <img 
                  src={getImage('help-button-6.png')} 
                  alt="Help Button" 
                  style={{ maxWidth: '400px', width: '100%', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Additional Accessories for Your Home or Mobile Medical Alert System
              </h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-8" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Medical Alert Bracelet - Emergency Alarm Wristband
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-1" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Added Protection Plan
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-9" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Medication Reminder
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="/accessories/accessory-4" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Emergency Lock Box
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Family Resources Section - Only for Fall Detection Pendant */}
      {isFallDetectionPendant && (
        <section style={{ padding: '1rem 2rem', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
                Family Resources
              </h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="https://www.alz.org" target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Alzheimer's Association - Dementia support
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="https://rusticpathways.com" target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Rustic Pathways - Educational travel for youth
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="https://www.aarp.org/caregiving" target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    AARP Family Caregiving - Caregiver resources
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="https://www.caring.com" target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    Caring.com - Senior care planning
                  </a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="https://www.ncoa.org" target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                    National Council on Aging - Active aging programs
                  </a>
                </li>
              </ul>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              {getImage('Resources-and-tools-medical-alert-system.png') && (
                <img 
                  src={getImage('Resources-and-tools-medical-alert-system.png')} 
                  alt="Resources and Tools" 
                  style={{ maxWidth: '300px', height: 'auto', borderRadius: '8px' }}
                />
              )}
            </div>
          </div>
        </section>
      )}

      {/* Purple Wristband Content Sections */}
      {isPurpleWristband && (
        <>
          {/* Pretty in Purple Section */}
          <section style={{ padding: '3rem 2rem', background: 'white' }}>
            <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
              <h2 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '2rem', color: '#333', textAlign: 'center' }}>
                Pretty in Purple
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', alignItems: 'center' }}>
                <div>
                  <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', lineHeight: '1.8', fontSize: '1.1rem' }}>
                    <li>Add some purple pizzazz with this vibrant grape wristband for your medical alert bracelet</li>
                    <li>Soft, light silicone material for comfort and durability</li>
                    <li>Waterproof material (can be worn in the shower)</li>
                    <li>Length: 10.5&quot;, width: 0.9&quot;</li>
                    <li>One size fits all</li>
                    <li>Designed to fit standard Alert1 home emergency button alarms</li>
                  </ul>
                  <p style={{ marginTop: '1.5rem', fontSize: '0.95rem', color: '#666', fontStyle: 'italic' }}>
                    <strong>Please Note:</strong> This wristband is not compatible with home fall detection or mobile medical alert help buttons.
                  </p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  {(getImage('alert1-purple-wristband.jpg')) && (
                    <img 
                      src={getImage('alert1-purple-wristband.jpg')} 
                      alt="Purple Wristband" 
                      style={{ width: '100%', maxWidth: '500px', height: 'auto', borderRadius: '8px' }}
                    />
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Additional Accessories Section */}
          <section style={{ padding: '3rem 2rem', background: '#f9f9f9' }}>
            <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
              <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '2rem', color: '#333' }}>
                Additional Accessories for Your Home or Mobile Medical Alert System
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
                <div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    <li style={{ marginBottom: '1rem' }}>
                      <a href="/accessories/accessory-2" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                        Monitored Smoke Detector
                      </a>
                    </li>
                    <li style={{ marginBottom: '1rem' }}>
                      <a href="/accessories/accessory-8" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                        Medical Alert Bracelet - Emergency Alarm Wristband
                      </a>
                    </li>
                    <li style={{ marginBottom: '1rem' }}>
                      <a href="/accessories/accessory-1" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                        Added Protection Plan
                      </a>
                    </li>
                    <li style={{ marginBottom: '1rem' }}>
                      <a href="/accessories/accessory-9" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                        Medication Reminder
                      </a>
                    </li>
                    <li style={{ marginBottom: '1rem' }}>
                      <a href="/accessories/accessory-4" style={{ color: '#0066cc', textDecoration: 'none', fontSize: '1.1rem' }}>
                        Emergency Lock Box
                      </a>
                    </li>
                  </ul>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {getImage('designer-collection-alert1-accessories.jpg') && (
                    <img 
                      src={getImage('designer-collection-alert1-accessories.jpg')} 
                      alt="Designer Collection Accessories" 
                      style={{ maxWidth: '400px', width: '100%', height: 'auto', borderRadius: '8px' }}
                    />
                  )}
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      <Footer />
    </div>
  );
};

export default AccessoryDetailPage;

